import React from "react";
import { getUserEnrollments } from "services";
import { useSnackbar } from "contexts";

/**
 * Maps a raw batchUserMapping into the batch shape the screens use — the same
 * mapping the desktop `SelectBatch` performs. The per-user scores live on the
 * mapping (`bum.communication`/`content`/...), while batch-level aggregates come
 * from `bum.batch`.
 */
function mapEnrollments(batchUserMappings = []) {
  return batchUserMappings.map((bum) => ({
    ...bum.batch,
    client: bum.client,
    batchUserMappingId: bum._id,
    forPathway: bum.batch?.forPathway || false,
    accessExpiresOn: bum?.accessExpiresOn,
    removed:
      bum.removed ||
      (bum?.accessExpiresOn && new Date(bum.accessExpiresOn) <= new Date()) ||
      false,
    user: {
      prevCommunication: bum.prevCommunication || 0,
      prevContent: bum.prevContent || 0,
      communication: bum.communication || 0,
      content: bum.content || 0,
      _id: bum.user,
      attempts: bum.attemptsCount,
    },
  }));
}

/**
 * Loads the user's batch enrollments and resolves the active batch.
 *
 * The initial batch uses the same default as the desktop `SelectBatch`: the
 * `selectedBatch` id in sessionStorage if it's still a valid enrollment,
 * otherwise the first batch. `selectBatch` persists the choice back to
 * sessionStorage, so switching batches on one screen carries over to the other.
 *
 * @returns {{ batches, batchData, loading, selectBatch }}
 */
export default function useSelectedBatch() {
  const snackbar = useSnackbar();

  const [batches, setBatches] = React.useState([]);
  const [batchData, setBatchData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let active = true;

    (async () => {
      try {
        setLoading(true);
        const { batchUserMappings } = await getUserEnrollments();
        if (!active) return;

        const mapped = mapEnrollments(batchUserMappings);
        setBatches(mapped);

        const storedId = sessionStorage.getItem("selectedBatch");
        const selected =
          mapped.find((b) => b._id === storedId) || mapped[0] || null;

        setBatchData(selected);
        if (selected?._id) sessionStorage.setItem("selectedBatch", selected._id);
      } catch (error) {
        console.error(error);
        if (active) snackbar.error("Uh Oh! Unable to load your batches");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same as the desktop `handleBatchChange` — persist the pick so it survives
  // navigation between Home and Performance.
  const selectBatch = React.useCallback((batch) => {
    if (!batch?._id) return;
    setBatchData(batch);
    sessionStorage.setItem("selectedBatch", batch._id);
  }, []);

  return { batches, batchData, loading, selectBatch };
}
